import {IFormSettingModel} from "../../../../../interfaces/form-settings.interface";
import React, {useEffect, useRef, useState} from "react";
import {
    CheckboxChangeEvent, Input,
    InputChangeEvent, RadioGroup,
    RadioGroupChangeEvent, TextArea,
    TextAreaChangeEvent
} from "@progress/kendo-react-inputs";
import {CalendarChangeEvent, DatePickerChangeEvent} from "@progress/kendo-react-dateinputs";
import {DropDownListChangeEvent} from "@progress/kendo-react-dropdowns";
import {IAgFormFourthSectionInterface} from "../../../../../interfaces/forms/AgForm/ag-form-fourth-section.interface";
import {PanelBar} from "@progress/kendo-react-layout/dist/npm/panelbar/PanelBar";
import {PanelBarItem} from "@progress/kendo-react-layout/dist/npm/panelbar/PanelBarItem";
import classes from "./AgForm.module.scss";
import {Error, Label} from "@progress/kendo-react-labels";
import {FormValidation} from "../InputValidator";
import SectionTitle from "../Shared/SectionTitle";
import { defaultRadioButtonData } from "../Shared/RadioButtonData";

interface GeneralFormType {
    isAdminPreview?: boolean,
    setFormDataFields: (form: IAgFormFourthSectionInterface) => void;
    formDataFields?:  IAgFormFourthSectionInterface,
    formSettings?: IFormSettingModel,
    setFormIsValid: (value: boolean) => void
}

export default function AgFormFourthSection(props: GeneralFormType): JSX.Element {
    const {setFormDataFields, formDataFields, formSettings, setFormIsValid} = props;

    const [isValid, setIsValid] = useState<boolean>(false);
    const [isFormTouched, setIsFormTouched] = useState<boolean>(false);
    const formRef = useRef<HTMLFormElement>(null);
    const title = <SectionTitle isFormTouched={isFormTouched} isValid={isValid} name={"Section 3: Property Description and Information"}/>;

    function changeValueHandler(
        event:
            | CheckboxChangeEvent
            | DatePickerChangeEvent
            | TextAreaChangeEvent
            | DropDownListChangeEvent
            | InputChangeEvent
            | RadioGroupChangeEvent
            | CalendarChangeEvent,
        field: string
    ) {
        setFormDataFields({
            ...formDataFields,
            [field]: event.value
        });
    }

    function changeValidator() {
        if (formRef.current) {
            setIsValid(formRef.current.checkValidity());
            setFormIsValid(formRef.current.checkValidity());
        }
    }

    function onTouch() {
        if (!isFormTouched) {
            setIsFormTouched(true);
        }
    }

    const { validationMessage, inputValidator } = FormValidation(formRef);

    useEffect(() => {
        changeValidator()
    }, [formDataFields, formRef.current]);

    return <>
        <PanelBar keepItemsMounted={true}>
            <PanelBarItem title={title} expanded={true}>
                <form name={"Name"} className={classes.sectionForm} ref={formRef} onFocus={onTouch}>
                    <div className="container">
                        <div className={classes.information}>Provide the descriptive information requested below for the property that is the subject of this application or attach last year’s tax statement, notice of appraised value or other correspondence identifying the property</div>
                        <div className={classes.item}>
                            <div className={classes.itemFields}>
                                <div className={`${classes.fieldsGroupW49}`}>
                                    <Label className={classes.Label}>
                                        Appraisal District Account Number
                                        <i className={"fa fa-asterisk text-danger"}></i>
                                    </Label>
                                    <div className="inputsGroup">
                                        <Input
                                            required={true}
                                            className={classes.kTextbox}
                                            name={"PropertyId"}
                                            disabled={formSettings?.isReadOnly}
                                            value={formDataFields?.["PropertyId"]}
                                            onChange={(e)=> changeValueHandler(e, "PropertyId")}
                                        />
                                        {!inputValidator("PropertyId") && (
                                            <Error >{validationMessage}</Error>
                                        )}
                                    </div>
                                </div>
                                <div className={`${classes.fieldsGroupW49}`}>
                                    <Label className={classes.Label}>
                                        Number of Acres (subject to this application)
                                        <i className={"fa fa-asterisk text-danger"}></i>
                                    </Label>
                                    <div className="inputsGroup">
                                        <Input
                                            required={true}
                                            className={classes.kTextbox}
                                            name={"TotalAcreage"}
                                            disabled={formSettings?.isReadOnly}
                                            value={formDataFields?.["TotalAcreage"]}
                                            onChange={(e)=> changeValueHandler(e, "TotalAcreage")}
                                        />
                                        {!inputValidator("TotalAcreage") && (
                                            <Error >{validationMessage}</Error>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={classes.item}>
                            <div className={`${classes.fieldsGroup}`}>
                                <Label className={classes.Label}>
                                    Legal description, abstract numbers, field numbers and/or plat numbers:
                                </Label>
                                <div className="inputsGroup">
                                    <TextArea
                                        name={"PropertyLegalDescription"}
                                        required={false}
                                        className={classes.kTextarea}
                                        autoSize={true}
                                        rows={2}
                                        disabled={formSettings?.isReadOnly}
                                        value={formDataFields?.PropertyLegalDescription}
                                        onChange={(e)=> changeValueHandler(e, "PropertyLegalDescription")}
                                    />
                                    {!inputValidator("PropertyLegalDescription") && (
                                        <Error >{validationMessage}</Error>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="help-text help-info">
                            <div id="undefinedHelpText"><b>Select the appropriate box in response to each question below.</b></div>
                        </div>
                        <div className={classes.item}>

                            <div className={classes.itemFields}>
                                <div className={`${classes.flexGroup} ${classes.fieldsGroupW100}`}>
                                    <Label className={classes.Label}>
                                        Did the district allow 1-d-1 appraisal on this  property last year?
                                        <i className={"fa fa-asterisk text-danger"}></i>
                                    </Label>
                                    <div className={classes.checkboxGroup}>
                                        <RadioGroup
                                            layout={"horizontal"}
                                            disabled={formSettings?.isReadOnly}
                                            data={defaultRadioButtonData}
                                            onChange={e => changeValueHandler(e, "DidDistrictAllow1D1Apraisal")}
                                            value={formDataFields?.DidDistrictAllow1D1Apraisal?.toString()}
                                        />
                                        {<Input
                                            name={"DidDistrictAllow1D1Apraisal"}
                                            required={true}
                                            disabled={formSettings?.isReadOnly}
                                            value={formDataFields?.DidDistrictAllow1D1Apraisal?.toString()}
                                            tabIndex={-1}
                                            style={{
                                                position:"absolute",
                                                opacity:0,
                                                height:0,
                                                width:0,
                                                pointerEvents:"none"
                                            }}
                                        />}
                                        {!inputValidator("DidDistrictAllow1D1Apraisal") && (
                                            <Error >{validationMessage}</Error>
                                        )}
                                    </div>
                                    { formDataFields?.DidDistrictAllow1D1Apraisal?.toString() === 'true' &&
                                        <div className="col-md-11">
                                            <div className="form-group form-field-type-help-text formio-component-undefinedHelpText ">
                                                <div className="help-text help-warning">
                                                    <div id="undefinedHelpText"> If yes, continue to question 2. If the land is used to manage wildlife, also complete Section 5.
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                    { formDataFields?.DidDistrictAllow1D1Apraisal?.toString() === 'false' &&
                                        <div className="col-md-11">
                                            <div className="form-group form-field-type-help-text formio-component-undefinedHelpText ">
                                                <div className="help-text help-warning">
                                                    <div id="undefinedHelpText"> If no, skip to Section 4 and complete all applicable questions.
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </div>
                            </div>
                            
                            <div className={classes.itemFields}>
                                <div className={`${classes.flexGroup} ${classes.fieldsGroupW100}`}>
                                    <Label className={classes.Label}>
                                        Has the ownership of the property changed since Jan. 1 of last year or since the last application was submitted?
                                        <i className={"fa fa-asterisk text-danger"}></i>
                                    </Label>
                                    <div className={classes.checkboxGroup}>
                                        <RadioGroup
                                            layout={"horizontal"}
                                            disabled={formSettings?.isReadOnly}
                                            data={defaultRadioButtonData}
                                            onChange={e => changeValueHandler(e, "HasPropertyOwnershipChanged")}
                                            value={formDataFields?.HasPropertyOwnershipChanged?.toString()}
                                        />
                                        {<Input
                                            name={"HasPropertyOwnershipChanged"}
                                            required={true}
                                            disabled={formSettings?.isReadOnly}
                                            value={formDataFields?.HasPropertyOwnershipChanged?.toString()}
                                            tabIndex={-1}
                                            style={{
                                                position:"absolute",
                                                opacity:0,
                                                height:0,
                                                width:0,
                                                pointerEvents:"none"
                                            }}
                                        />}
                                        {!inputValidator("HasPropertyOwnershipChanged") && (
                                            <Error >{validationMessage}</Error>
                                        )}
                                    </div>
                                    { formDataFields?.HasPropertyOwnershipChanged?.toString() === 'true' &&
                                        <div className="col-md-11">
                                            <div className="form-group form-field-type-help-text formio-component-undefinedHelpText ">
                                                <div className="help-text help-warning">
                                                    <div id="undefinedHelpText">The new
                                                        owner must complete all applicable questions and, if the land is
                                                        used to manage wildlife, sections 4 and 5 must be completed.
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                    { formDataFields?.HasPropertyOwnershipChanged?.toString() === 'false'  &&
                                        <div className="col-md-11">
                                            <div className="form-group form-field-type-help-text formio-component-undefinedHelpText ">
                                                <div className="help-text help-warning">
                                                    <div id="undefinedHelpText">If no, skip to question 5 then complete only those parts of Section 4 and 5 
                                                        that have changed since the earlier application or any information in Sections 4 and 5 the chief appraiser requests.
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </div>
                            </div>
                            <div className={classes.itemFields}>
                                <div className={`${classes.flexGroup} ${classes.fieldsGroupW100}`}>
                                    <Label className={classes.Label}>
                                        Has the former owner passed away?
                                        <i className={"fa fa-asterisk text-danger"}></i>
                                    </Label>
                                    <div className={classes.checkboxGroup}>
                                        <RadioGroup
                                            layout={"horizontal"}
                                            disabled={formSettings?.isReadOnly}
                                            data={defaultRadioButtonData}
                                            onChange={e => changeValueHandler(e, "HasFormerOwnerPassedAway")}
                                            value={formDataFields?.HasFormerOwnerPassedAway?.toString()}
                                        />
                                        {<Input
                                            name={"HasFormerOwnerPassedAway"}
                                            required={true}
                                            disabled={formSettings?.isReadOnly}
                                            value={formDataFields?.HasFormerOwnerPassedAway?.toString()}
                                            tabIndex={-1}
                                            style={{
                                                position:"absolute",
                                                opacity:0,
                                                height:0,
                                                width:0,
                                                pointerEvents:"none"
                                            }}
                                        />}
                                        {!inputValidator("HasFormerOwnerPassedAway") && (
                                            <Error >{validationMessage}</Error>
                                        )}
                                    </div>
                                </div>
                                    { formDataFields?.HasFormerOwnerPassedAway?.toString() === 'true' &&
                                          <div className={`${classes.flexGroup} ${classes.fieldsGroupW100}`}>
                                          <Label className={classes.Label}>
                                          If yes, are you the surviving spouse of the former owner? 
                                          </Label>
                                          <div className={classes.checkboxGroup}>
                                              <RadioGroup
                                                  layout={"horizontal"}
                                                  disabled={formSettings?.isReadOnly}
                                                  data={defaultRadioButtonData}
                                                  onChange={e => changeValueHandler(e, "IsSurvivingSpouse")}
                                                  value={formDataFields?.IsSurvivingSpouse?.toString()}
                                              />
                                          </div>
                                      </div>
                                    }
                                
                            </div>
                            <div className={classes.itemFields}>
                                <div className={`${classes.flexGroup} ${classes.fieldsGroupW100}`}>
                                    <Label className={classes.Label}>
                                        Last year, was 1-d-1 appraisal allowed on this property by the chief appraiser of this appraisal district?
                                        <i className={"fa fa-asterisk text-danger"}></i>
                                    </Label>
                                    <div className="inputsGroup">
                                        <RadioGroup
                                            layout={"horizontal"}
                                            name={"WasAllowed1D1Appraisal"}
                                            disabled={formSettings?.isReadOnly}
                                            data={defaultRadioButtonData}
                                            onChange={e => changeValueHandler(e, "WasAllowed1D1Appraisal")}
                                            value={formDataFields?.WasAllowed1D1Appraisal?.toString()}
                                        />
                                        {<Input
                                            name={"WasAllowed1D1Appraisal"}
                                            required={true}
                                            disabled={formSettings?.isReadOnly}
                                            value={formDataFields?.WasAllowed1D1Appraisal?.toString()}
                                            tabIndex={-1}
                                            style={{
                                                position:"absolute",
                                                opacity:0,
                                                height:0,
                                                width:0,
                                                pointerEvents:"none"
                                            }}
                                        />}
                                        {!inputValidator("WasAllowed1D1Appraisal") && (
                                            <Error >{validationMessage}</Error>
                                        )}
                                    </div>
                                    { formDataFields?.WasAllowed1D1Appraisal?.toString() === 'true' &&
                                        <div className="col-md-11">
                                            <div>
                                                <div className="help-text help-warning" >
                                                    <div id="undefinedHelpText">
                                                        Complete only those parts of sections 4 and 5 that have changed
                                                        since your earlier application or any information in sections 4
                                                        and 5 requested by the chief appraiser.
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                    { formDataFields?.WasAllowed1D1Appraisal?.toString() === 'false' &&
                                        <div className="col-md-11">
                                            <div className="form-group form-field-type-help-text formio-component-undefinedHelpText ">
                                                <div className="help-text help-warning">
                                                    <div id="undefinedHelpText">
                                                        All applicable questions must be completed and, if the land
                                                        is used to manage wildlife, sections 4 and 5 must be
                                                        completed.
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </div>
                            </div>
                            
                            <div className={classes.itemFields}>
                                <div className={`${classes.flexGroup} ${classes.fieldsGroupW100}`}>
                                    <Label className={classes.Label}>
                                        Is the new owner using the land in materially the same way as in the preceding year and is the use overseen or conducted by the 
                                    </Label>
                                    <Label className={classes.Label}>
                                    same individuals who oversaw or conducted that use in the preceeding year?
                                    <i className={"fa fa-asterisk text-danger"}></i>
                                    </Label>
                                    <div className="inputsGroup">
                                        <RadioGroup
                                            layout={"horizontal"}
                                            name={"IsNewOwnerUsingtheLandinMaterially"}
                                            disabled={formSettings?.isReadOnly}
                                            data={defaultRadioButtonData}
                                            onChange={e => changeValueHandler(e, "IsNewOwnerUsingtheLandinMaterially")}
                                            value={formDataFields?.IsNewOwnerUsingtheLandinMaterially?.toString()}
                                        />
                                        {<Input
                                            name={"IsNewOwnerUsingtheLandinMaterially"}
                                            required={true}
                                            disabled={formSettings?.isReadOnly}
                                            value={formDataFields?.IsNewOwnerUsingtheLandinMaterially?.toString()}
                                            tabIndex={-1}
                                            style={{
                                                position:"absolute",
                                                opacity:0,
                                                height:0,
                                                width:0,
                                                pointerEvents:"none"
                                            }}
                                        />}
                                        {!inputValidator("IsNewOwnerUsingtheLandinMaterially") && (
                                            <Error >{validationMessage}</Error>
                                        )}
                                    </div>
                                    { formDataFields?.IsNewOwnerUsingtheLandinMaterially?.toString() === 'true' &&
                                        <div className="col-md-11">
                                            <div>
                                                <div className="help-text help-warning" >
                                                    <div id="undefinedHelpText">
                                                        If yes to questions 3 and/or 4, complete only those parts of Sections 4 and 5 that have changed since the earlier application or any 
                                                        information in Sections 4 and 5 the chief appraiser requests. 
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                    { formDataFields?.IsNewOwnerUsingtheLandinMaterially?.toString() === 'false' &&
                                        <div className="col-md-11">
                                            <div className="form-group form-field-type-help-text formio-component-undefinedHelpText ">
                                                <div className="help-text help-warning">
                                                    <div id="undefinedHelpText">
                                                        If no to both questions 3 and 4, the new owner must complete all applicable questions in Section 4 and, if the land is used to manage wildlife, complete Section 5.
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </div>
                            </div>

                            <div className={classes.itemFields}>
                                <div className={`${classes.flexGroup} ${classes.fieldsGroupW100}`}>
                                    <Label className={classes.Label}>
                                        Is this property located within the corporate limits of a city or town?
                                        <i className={"fa fa-asterisk text-danger"}></i>
                                    </Label>
                                    <div className="inputsGroup">
                                        <RadioGroup
                                            layout={"horizontal"}
                                            name={"IsThisPropertyLocatedWithinTheCorporateLimitsOfaCityorTown"}
                                            disabled={formSettings?.isReadOnly}
                                            data={defaultRadioButtonData}
                                            onChange={e => changeValueHandler(e, "IsThisPropertyLocatedWithinTheCorporateLimitsOfaCityorTown")}
                                            value={formDataFields?.IsThisPropertyLocatedWithinTheCorporateLimitsOfaCityorTown?.toString()}
                                        />
                                        {<Input
                                            name={"IsThisPropertyLocatedWithinTheCorporateLimitsOfaCityorTown"}
                                            required={true}
                                            disabled={formSettings?.isReadOnly}
                                            value={formDataFields?.IsThisPropertyLocatedWithinTheCorporateLimitsOfaCityorTown?.toString()}
                                            tabIndex={-1}
                                            style={{
                                                position:"absolute",
                                                opacity:0,
                                                height:0,
                                                width:0,
                                                pointerEvents:"none"
                                            }}
                                        />}
										{!inputValidator("IsThisPropertyLocatedWithinTheCorporateLimitsOfaCityorTown") && (
                                            <Error >{validationMessage}</Error>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </PanelBarItem>
        </PanelBar>
    </>
}
